/**
 * Anesthesiologist Filter Bar Component
 *
 * A filter bar with debounced name search.
 * Used by the Anesthesiologists list for server-side filtering.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Box, TextField, Button } from '@material-ui/core';

export interface AnesthesiologistFilter {
  name?: string;
}

interface AnesthesiologistFilterBarProps {
  filter?: AnesthesiologistFilter;
  value?: AnesthesiologistFilter;
  onChange: (filter: AnesthesiologistFilter) => void;
}

const AnesthesiologistFilterBar: React.FC<AnesthesiologistFilterBarProps> = ({ filter, value, onChange }) => {
  const effectiveFilter = (filter ?? value ?? {}) as AnesthesiologistFilter;

  // Local state for name input to support debounced updates
  const [nameInput, setNameInput] = useState<string>(effectiveFilter.name || '');
  const debounceTimeoutRef = useRef<number | undefined>(undefined);

  // Keep local input in sync when external filter changes (e.g., via URL)
  useEffect(() => {
    setNameInput(effectiveFilter.name || '');
  }, [effectiveFilter.name]);

  // Cleanup timeout on unmount
  useEffect(
    () => () => {
      if (debounceTimeoutRef.current !== undefined) {
        window.clearTimeout(debounceTimeoutRef.current);
      }
    },
    []
  );

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    setNameInput(inputValue);

    // Clear any pending update
    if (debounceTimeoutRef.current !== undefined) {
      window.clearTimeout(debounceTimeoutRef.current);
    }

    // Debounce updates to avoid spamming the server while typing
    debounceTimeoutRef.current = window.setTimeout(() => {
      const trimmed = inputValue.trim();
      const nextFilter: AnesthesiologistFilter = { ...effectiveFilter };

      if (trimmed.length >= 2) {
        nextFilter.name = trimmed;
      } else {
        delete nextFilter.name;
      }

      onChange(nextFilter);
    }, 300);
  };

  const handleClearAll = () => {
    setNameInput('');
    onChange({});
  };

  const hasActiveFilters = Boolean(effectiveFilter.name);

  return (
    <Box display="flex" flexWrap="wrap" alignItems="center" gridGap={16} mb={1}>
      <TextField
        label="Name"
        variant="outlined"
        size="small"
        value={nameInput}
        onChange={handleNameChange}
        placeholder="Search by name..."
        style={{ minWidth: 200 }}
      />
      {hasActiveFilters && (
        <Button size="small" onClick={handleClearAll}>
          Clear All
        </Button>
      )}
    </Box>
  );
};

AnesthesiologistFilterBar.displayName = 'AnesthesiologistFilterBar';

export default AnesthesiologistFilterBar;
